import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function FoodTrucksInVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "best-food-trucks-in-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Top Food Trucks",
    "section-2": "Seasonal Events",
    "local-regulations": "Local Regulations",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://vancouver.ca/doing-business/food-trucks.aspx",
      label: "City of Vancouver – Street Food Program",
    },
    {
      url: "https://www.destinationvancouver.com",
      label: "Tourism Vancouver",
    },
    {
      url: "https://streetfoodapp.com/vancouver",
      label: "Street Food App – Vancouver",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  const locations = [
    {
      id: "section-1-japadog",
      lat: 49.2835,
      lng: -123.1153,
      popupText: "Japadog",
    },
    {
      id: "section-1-moms-grilled-cheese",
      lat: 49.2817,
      lng: -123.1206,
      popupText: "Mom’s Grilled Cheese",
    },
    {
      id: "section-1-tacofino",
      lat: 49.28,
      lng: -123.1147,
      popupText: "Tacofino",
    },
    {
      id: "section-1-cazba-express",
      lat: 49.2805,
      lng: -123.1199,
      popupText: "Cazba Express",
    },
    {
      id: "section-1-via-tevere",
      lat: 49.2772,
      lng: -123.1213,
      popupText: "Via Tevere Neapolitan Express",
    },
    {
      id: "section-2-street-food-city",
      lat: 49.2816,
      lng: -123.1198,
      popupText: "Street Food City",
    },
    {
      id: "section-2-summer-food-truck-fest",
      lat: 49.2768,
      lng: -123.1143,
      popupText: "Summer Food Truck Fest",
    },
    {
      id: "section-2-richmond-night-market",
      lat: 49.1867,
      lng: -123.1210,
      popupText: "Richmond Night Market",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Best Food Trucks in Vancouver: A Practical Guide</title>
        <meta
          name="description"
          content="Discover Vancouver’s vibrant food truck scene with this practical guide. Find top locations, costs, operating hours, and helpful tips!"
        />
        <meta
          name="keywords"
          content="food trucks, Vancouver, street food, dining, local regulations"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/best-food-trucks-in-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Best Food Trucks in Vancouver: A Practical Guide"
        />
        <meta
          property="og:description"
          content="Explore the top food trucks in Vancouver, complete with locations, operating hours, and insider tips."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/best-food-trucks-in-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/best-food-trucks-in-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Best Food Trucks in Vancouver: A Practical Guide"
        />
        <meta
          name="twitter:description"
          content="Check out our comprehensive guide to Vancouver's must-visit food trucks."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/best-food-trucks-in-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Vancouver food truck scene"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s food truck scene is a delicious reflection of the
              city’s diversity, offering everything from fusion hot dogs to
              Neapolitan-style pizzas on the go. In this guide, we provide
              actionable tips, coordinates, and operating hours for some of the
              best trucks, plus insights on seasonal events, local regulations,
              and FAQs. Whether you’re a local or just visiting, these mobile
              eateries make exploring Vancouver’s culinary landscape even more
              exciting.
            </p>

            {/* SECTION: Top Food Trucks */}
            <h2 id="section-1">Top Food Trucks</h2>

            <p>
              Vancouver boasts an incredible variety of food trucks. Below are
              five favorites, each bringing something unique to the table.
            </p>

            <p
              ref={(el) => setSpotRef("section-1-japadog", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Japadog</strong>{" "}
              <Marker
                coordY={49.2835}
                coordX={-123.1153}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Often found near Robson Street, Japadog offers fusion hot dogs
              topped with teriyaki sauce, seaweed, and more. Operating hours are
              roughly 11 AM to 7 PM. Expect to spend CAD 6–10 on a specialty
              dog.
            </p>

            <p
              ref={(el) => setSpotRef("section-1-moms-grilled-cheese", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Mom’s Grilled Cheese</strong>{" "}
              <Marker
                coordY={49.2817}
                coordX={-123.1206}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Parked near the Vancouver Art Gallery, this truck specializes in
              crispy, ooey-gooey grilled cheese sandwiches paired with retro
              sodas. Prices range from CAD 5–9, and they’re usually open Monday
              to Friday, 11 AM to 5 PM.
            </p>

            <p
              ref={(el) => setSpotRef("section-1-tacofino", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Tacofino</strong>{" "}
              <Marker
                coordY={49.28}
                coordX={-123.1147}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Known for their famous fish tacos and fresh salsas, Tacofino
              frequents areas near Burrard Station. Prices are CAD 4–7 per taco,
              and lines can get long around noon.
            </p>

            <p
              ref={(el) => setSpotRef("section-1-cazba-express", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Cazba Express</strong>{" "}
              <Marker
                coordY={49.2805}
                coordX={-123.1199}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Serving up Persian-inspired cuisine like koobideh kebabs and
              falafel wraps. Located often near the Vancouver Art Gallery as
              well. Expect a meal to cost about CAD 8–12.
            </p>

            <p
              ref={(el) => setSpotRef("section-1-via-tevere", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Via Tevere Neapolitan Express</strong>{" "}
              <Marker
                coordY={49.2772}
                coordX={-123.1213}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Typically found around Canada Place or Gastown, this truck
              brings authentic Neapolitan-style pizza to the streets. Each
              wood-fired pie runs around CAD 10–14.
            </p>

            {/* SECTION: Seasonal Events */}
            <h2 id="section-2">Seasonal Food Truck Events</h2>
            <p>
              Beyond individual trucks, Vancouver hosts several food truck
              events throughout the year, making it easy to sample multiple
              cuisines in one place.
            </p>

            <p
              ref={(el) => setSpotRef("section-2-street-food-city", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Street Food City</strong>{" "}
              <Marker
                coordY={49.2816}
                coordX={-123.1198}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Held annually in mid-winter outside the Vancouver Art Gallery,
              this week-long festival features a wide array of food trucks and
              cuisines.
            </p>

            <p
              ref={(el) => setSpotRef("section-2-summer-food-truck-fest", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Summer Food Truck Fest</strong>{" "}
              <Marker
                coordY={49.2768}
                coordX={-123.1143}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Usually near Waterfront Station during summer weekends, expect
              live music, artisan vendors, and an assortment of street food from
              around the globe.
            </p>

            <p
              ref={(el) => setSpotRef("section-2-richmond-night-market", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Richmond Night Market</strong>{" "}
              <Marker
                coordY={49.1867}
                coordX={-123.1210}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – While not in Vancouver proper, this massive event is only a
              short drive away. Dozens of stands and trucks offer Asian-inspired
              street fare, adding to the food truck scene’s diversity.
            </p>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              All Vancouver food trucks operate under city-specific guidelines
              and licenses. Trucks must adhere to strict health inspections by
              Vancouver Coastal Health, and operators must follow municipal
              parking regulations. For exact details on permissible locations
              and permit requirements, refer to the City of Vancouver’s official
              website. 
            </p>
            <p>
              <strong>Best Practices for Patrons:</strong> 
            </p>
            <ul>
              <li>Dispose of waste in designated bins.</li>
              <li>Avoid blocking sidewalks or roadways while queuing.</li>
              <li>Check the food truck’s posted inspection notice.</li>
            </ul>
            <p>
              <em>
                Disclaimer: Regulations and business hours can change. Always
                check official resources or contact local authorities for the
                most up-to-date information.
              </em>
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Do most food trucks accept credit cards?</h3>
              <p>
                Yes, though some smaller vendors may be cash-only. It’s best to
                have both payment methods on hand.
              </p>

              <h3>2. How do I find a truck’s exact location?</h3>
              <p>
                Many trucks post real-time location updates on social media
                platforms. Apps like the Street Food App – Vancouver can also
                help.
              </p>

              <h3>3. Are there vegetarian or vegan options available?</h3>
              <p>
                Absolutely. Many trucks, including Cazba Express and Tacofino,
                offer plant-based or vegetarian-friendly items.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Whether you’re grabbing a quick lunch downtown or exploring a
              weekend festival, Vancouver’s food truck scene delivers variety,
              flavor, and convenience. From the iconic Japadog to specialty
              pizza trucks, there’s something to satisfy every palate. Stay
              updated on social media, check out seasonal events, and enjoy
              exploring this delicious facet of the city’s dining culture!
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}